import React from 'react';
import { useLang } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import {
  ShieldCheck, MonitorSmartphone, Cloud, CalendarDays,
  Lock, FileText, AtSign, Activity, Mail, KeyRound,
  Fingerprint, Smartphone
} from 'lucide-react';

const featureIcons = {
  antispam: ShieldCheck,
  sync: MonitorSmartphone,
  cloud: Cloud,
  calendar: CalendarDays,
  dsgvo: Lock,
  office: FileText,
  alias: AtSign,
  uptime: Activity,
  email: Mail,
  s_mime: KeyRound,
  twofa: Fingerprint,
  mobile: Smartphone,
};

const featureStyles = {
  antispam: { color: 'text-neon', glow: 'shadow-[0_0_30px_rgba(212,255,0,0.15)]', border: 'hover:border-neon/40', bg: 'bg-neon/5' },
  sync: { color: 'text-cyan', glow: 'shadow-[0_0_30px_rgba(0,240,255,0.15)]', border: 'hover:border-cyan/40', bg: 'bg-cyan/5' },
  cloud: { color: 'text-pink', glow: 'shadow-[0_0_30px_rgba(255,0,127,0.15)]', border: 'hover:border-pink/40', bg: 'bg-pink/5' },
  calendar: { color: 'text-neon', glow: 'shadow-[0_0_30px_rgba(212,255,0,0.15)]', border: 'hover:border-neon/40', bg: 'bg-neon/5' },
  dsgvo: { color: 'text-cyan', glow: 'shadow-[0_0_30px_rgba(0,240,255,0.15)]', border: 'hover:border-cyan/40', bg: 'bg-cyan/5' },
  office: { color: 'text-pink', glow: 'shadow-[0_0_30px_rgba(255,0,127,0.15)]', border: 'hover:border-pink/40', bg: 'bg-pink/5' },
  alias: { color: 'text-neon', glow: 'shadow-[0_0_30px_rgba(212,255,0,0.15)]', border: 'hover:border-neon/40', bg: 'bg-neon/5' },
  uptime: { color: 'text-cyan', glow: 'shadow-[0_0_30px_rgba(0,240,255,0.15)]', border: 'hover:border-cyan/40', bg: 'bg-cyan/5' },
  email: { color: 'text-pink', glow: 'shadow-[0_0_30px_rgba(255,0,127,0.15)]', border: 'hover:border-pink/40', bg: 'bg-pink/5' },
  s_mime: { color: 'text-neon', glow: 'shadow-[0_0_30px_rgba(212,255,0,0.15)]', border: 'hover:border-neon/40', bg: 'bg-neon/5' },
  twofa: { color: 'text-cyan', glow: 'shadow-[0_0_30px_rgba(0,240,255,0.15)]', border: 'hover:border-cyan/40', bg: 'bg-cyan/5' },
  mobile: { color: 'text-pink', glow: 'shadow-[0_0_30px_rgba(255,0,127,0.15)]', border: 'hover:border-pink/40', bg: 'bg-pink/5' },
};

const gridSpans = [
  'md:col-span-4 lg:col-span-5',
  'md:col-span-4 lg:col-span-4',
  'md:col-span-4 lg:col-span-3',
  'md:col-span-6 lg:col-span-7',
  'md:col-span-6 lg:col-span-5',
  'md:col-span-4 lg:col-span-3',
  'md:col-span-4 lg:col-span-5',
  'md:col-span-4 lg:col-span-4',
  'md:col-span-6 lg:col-span-4',
  'md:col-span-6 lg:col-span-4',
  'md:col-span-6 lg:col-span-4',
  'md:col-span-6 lg:col-span-12',
];

export default function FeaturesGrid() {
  const { t } = useLang();
  const featureKeys = Object.keys(featureIcons);

  return (
    <section id="features" data-testid="features-section" className="py-24 lg:py-32 bg-[#050505] relative grid-bg">
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-neon/5 rounded-full blur-[200px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink/5 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-neon mb-4 bg-neon/10 border border-neon/20 rounded-full px-4 py-1.5">
            <span className="w-1.5 h-1.5 bg-neon rounded-full" />
            {t('features.overline')}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-5">
            {t('features.title')}
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 font-light max-w-2xl leading-relaxed">
            {t('features.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 lg:gap-4">
          {featureKeys.map((key, i) => {
            const Icon = featureIcons[key];
            const style = featureStyles[key];
            const isWide = gridSpans[i].includes('col-span-12');
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`${gridSpans[i]} col-span-1 bg-surface/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-6 lg:p-8 hover:-translate-y-1.5 ${style.border} transition-all duration-500 group hover:${style.glow} relative overflow-hidden ${isWide ? 'flex items-center gap-6' : ''}`}
                data-testid={`feature-card-${key}`}
              >
                {/* Subtle corner glow */}
                <div className={`absolute -top-12 -right-12 w-24 h-24 ${style.bg} rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
                
                <div className={`${isWide ? 'flex-shrink-0' : 'mb-4'}`}>
                  <div className={`w-12 h-12 rounded-xl ${style.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${style.color}`} />
                  </div>
                </div>
                <div>
                  <h3 className="font-heading text-base lg:text-lg font-bold text-white mb-1.5">
                    {t(`features.items.${key}.title`)}
                  </h3>
                  <p className="text-sm text-zinc-500 font-light leading-relaxed">
                    {t(`features.items.${key}.desc`)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
